import React, { useState, useEffect, useRef } from "react"
import { useShoppingCart } from "use-shopping-cart"

// H7 - Flexibilidad: cantidad editable directamente (click en el número)
const CartItem = ({
  item,
  onRemove,
  onIncrement,
  onDecrement,
  onSetQuantity,
}) => {
  const [editingQty, setEditingQty] = useState(false)
  const [inputVal, setInputVal] = useState(String(item.quantity))

  const itemTotalPrice = item.price * item.quantity
  const formattedItemTotal = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(itemTotalPrice / 100)

  const handleQtyBlur = () => {
    setEditingQty(false)
    const parsed = parseInt(inputVal, 10)
    if (
      !isNaN(parsed) &&
      parsed >= 1 &&
      parsed <= 99 &&
      parsed !== item.quantity
    ) {
      onSetQuantity(parsed)
    } else {
      setInputVal(String(item.quantity))
    }
  }

  const handleQtyKeyDown = (e) => {
    if (e.key === "Enter") handleQtyBlur()
    if (e.key === "Escape") {
      setEditingQty(false)
      setInputVal(String(item.quantity))
    }
  }

  // Sync input when item.quantity changes externally
  useEffect(() => {
    if (!editingQty) setInputVal(String(item.quantity))
  }, [item.quantity, editingQty])

  return (
    <div className="cart-item">
      <div className="cart-item-image">
        {item.image && <img src={item.image} alt={item.name} />}
      </div>
      <div className="cart-item-details">
        <h3 className="cart-item-name">{item.name}</h3>
        <div className="cart-item-price-container">
          <span className="cart-item-price-unit">{item.formattedPrice}</span>
          <span className="cart-item-price-separator">×</span>
          <span className="cart-item-quantity-label">
            {item.quantity} {item.quantity === 1 ? "unidad" : "unidades"}
          </span>
          <span className="cart-item-price-separator">=</span>
          <span className="cart-item-price-total">{formattedItemTotal}</span>
        </div>
        <div className="cart-item-controls">
          <button
            className="cart-item-button"
            onClick={onDecrement}
            aria-label="Disminuir cantidad"
          >
            −
          </button>

          {/* H7 - Click en el número para editar directamente */}
          {editingQty ? (
            <input
              className="cart-item-quantity-input"
              type="number"
              value={inputVal}
              min={1}
              max={99}
              onChange={(e) => setInputVal(e.target.value)}
              onBlur={handleQtyBlur}
              onKeyDown={handleQtyKeyDown}
              autoFocus
              aria-label="Cantidad"
            />
          ) : (
            <span
              className="cart-item-quantity cart-item-quantity--editable"
              onClick={() => {
                setEditingQty(true)
                setInputVal(String(item.quantity))
              }}
              title="Haz clic para editar la cantidad"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setEditingQty(true)}
            >
              {item.quantity}
            </span>
          )}

          <button
            className="cart-item-button"
            onClick={onIncrement}
            aria-label="Aumentar cantidad"
          >
            +
          </button>
          <button
            className="cart-item-remove"
            onClick={onRemove}
            aria-label="Eliminar item"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const CartSidebar = ({ onClose }) => {
  const {
    cartDetails,
    removeItem,
    incrementItem,
    decrementItem,
    addItem,
    setItemQuantity,
    formattedTotalPrice,
  } = useShoppingCart()

  // H3 - Control del usuario: undo al eliminar item
  const [undoItem, setUndoItem] = useState(null)
  const undoTimerRef = useRef(null)

  const cartItems = Object.values(cartDetails || {})

  const handleRemove = (item) => {
    const snapshot = { ...item }
    removeItem(item.id)
    setUndoItem(snapshot)

    clearTimeout(undoTimerRef.current)
    undoTimerRef.current = setTimeout(() => {
      setUndoItem(null)
    }, 4000)
  }

  const handleUndo = () => {
    if (!undoItem) return
    addItem(
      {
        id: undoItem.id,
        name: undoItem.name,
        price: undoItem.price,
        image: undoItem.image || "",
        currency: undoItem.currency || "MXN",
        description:
          typeof undoItem.description === "string"
            ? undoItem.description
            : undoItem.name,
      },
      { count: undoItem.quantity }
    )
    clearTimeout(undoTimerRef.current)
    setUndoItem(null)
  }

  const handleSetQuantity = (itemId, qty) => {
    setItemQuantity(itemId, qty)
  }

  useEffect(() => {
    return () => clearTimeout(undoTimerRef.current)
  }, [])

  return (
    <div className="cart-sidebar">
      <div className="cart-sidebar-header">
        <h2>Carrito de Compras</h2>
        <button
          className="cart-close-button"
          onClick={onClose}
          aria-label="Cerrar carrito"
        >
          ×
        </button>
      </div>

      <div className="cart-sidebar-content">
        {cartItems.length === 0 ? (
          <div className="cart-empty">
            <p>Tu carrito está vacío</p>
          </div>
        ) : (
          <div className="cart-items-list">
            {cartItems.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onRemove={() => handleRemove(item)}
                onIncrement={() => incrementItem(item.id)}
                onDecrement={() => decrementItem(item.id)}
                onSetQuantity={(qty) => handleSetQuantity(item.id, qty)}
              />
            ))}
          </div>
        )}
      </div>

      {/* H3 - Toast de deshacer eliminación */}
      {undoItem && (
        <div className="cart-undo-toast">
          <span className="cart-undo-toast-text">
            "{undoItem.name}" eliminado
          </span>
          <button className="cart-undo-toast-btn" onClick={handleUndo}>
            Deshacer
          </button>
        </div>
      )}

      {cartItems.length > 0 && (
        <div className="cart-sidebar-footer">
          <div className="cart-total">
            <span>Total:</span>
            <span className="cart-total-price">{formattedTotalPrice}</span>
          </div>
          <button
            className="cart-checkout-button"
            onClick={() => {
              onClose()
              window.location.href = "/cart"
            }}
          >
            Ir a Checkout
          </button>
        </div>
      )}
    </div>
  )
}

const CartIcon = () => {
  const { cartCount } = useShoppingCart()
  const [isOpen, setIsOpen] = useState(false)

  const toggleCart = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    setIsOpen((prev) => !prev)
  }

  const closeCart = () => {
    setIsOpen(false)
  }

  // Body-scroll lock simple: sólo overflow: hidden. La técnica anterior
  // (position: fixed + top: -Xpx) causaba jank y scroll perdido al
  // abrir/cerrar varias veces seguidas.
  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  // Cerrar con tecla Escape (mejora UX y accesibilidad).
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen])

  return (
    <>
      <button
        type="button"
        className="cart-icon-button"
        onClick={toggleCart}
        aria-label="Abrir carrito"
      >
        <svg
          className="cart-icon-svg"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        {cartCount > 0 && <span className="cart-icon-badge">{cartCount}</span>}
      </button>
      {isOpen && (
        <>
          <div className="cart-overlay" onClick={closeCart}></div>
          <div
            className="cart-sidebar-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
            <CartSidebar onClose={closeCart} />
          </div>
        </>
      )}
    </>
  )
}

export default CartIcon
